import { getTourDistanceMeters, getTourElevationGainMeters } from '@/services/tourStatsService'
import {
  calculateHaversineDistance,
  calculateTrackElevation,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import type { Segment, Tour, Track } from '@/types/tour'

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const roundPositiveOrNull = (value: number | null): number | null =>
  value !== null && value > 0 ? Math.round(value) : null

function calculateSegmentDistance(segment: Segment): number | null {
  if (isFiniteNumber(segment.trackLength)) {
    return segment.trackLength
  }

  let total = 0
  let hasDistance = false
  for (let i = 1; i < segment.points.length; i += 1) {
    const previous = segment.points[i - 1]
    const current = segment.points[i]
    total += calculateHaversineDistance(previous.lat, previous.lon, current.lat, current.lon)
    hasDistance = true
  }

  return hasDistance ? total : null
}

function calculateSegmentElevationDelta(segment: Segment): {
  gain: number | null
  loss: number | null
} {
  const pointMetrics =
    isFiniteNumber(segment.filteredAscend) && isFiniteNumber(segment.filteredDescend)
      ? null
      : calculateTrackElevation({ clientId: '__segment_metrics__', segments: [segment] } as Track)

  return {
    gain: isFiniteNumber(segment.filteredAscend)
      ? roundPositiveOrNull(segment.filteredAscend)
      : (pointMetrics?.elevationGain ?? null),
    loss: isFiniteNumber(segment.filteredDescend)
      ? roundPositiveOrNull(segment.filteredDescend)
      : (pointMetrics?.elevationLoss ?? null),
  }
}

export function refreshTrackMetrics(track: Track): void {
  const pointMetrics = calculateTrackElevation(track)
  let totalDistance = 0
  let hasDistance = false
  let totalElevationGain: number | null = null
  let totalElevationLoss: number | null = null

  for (const segment of track.segments) {
    const segmentDistance = calculateSegmentDistance(segment)
    if (segmentDistance !== null) {
      totalDistance += segmentDistance
      hasDistance = true
    }

    const segmentElevation = calculateSegmentElevationDelta(segment)
    if (segmentElevation.gain !== null) {
      totalElevationGain = (totalElevationGain ?? 0) + segmentElevation.gain
    }
    if (segmentElevation.loss !== null) {
      totalElevationLoss = (totalElevationLoss ?? 0) + segmentElevation.loss
    }
  }

  track.metrics = {
    elevationMin: pointMetrics.elevationMin,
    elevationMax: pointMetrics.elevationMax,
    elevationGain: roundPositiveOrNull(totalElevationGain) ?? pointMetrics.elevationGain,
    elevationLoss: roundPositiveOrNull(totalElevationLoss) ?? pointMetrics.elevationLoss,
    distance: hasDistance && totalDistance > 0 ? Math.round(totalDistance) : pointMetrics.distance,
    timeMoving: getTrackTimeMoving({ ...track, metrics: undefined }),
    timeElapsed: getTrackTimeElapsed({ ...track, metrics: undefined }),
  }
}

export function refreshTourTrackStats(tour: Tour): void {
  tour.distance = getTourDistanceMeters(tour)
  tour.elevationGain = getTourElevationGainMeters(tour) ?? null

  let totalTimeMoving: number | null = null
  let totalTimeElapsed: number | null = null
  for (const track of tour.tracks) {
    const timeMoving = getTrackTimeMoving(track)
    if (timeMoving !== null && timeMoving !== undefined) {
      totalTimeMoving = (totalTimeMoving ?? 0) + timeMoving
    }

    const timeElapsed = getTrackTimeElapsed(track)
    if (timeElapsed !== null && timeElapsed !== undefined) {
      totalTimeElapsed = (totalTimeElapsed ?? 0) + timeElapsed
    }
  }

  tour.time_moving = totalTimeMoving
  tour.time_elapsed = totalTimeElapsed
}
